import styled, { css } from "styled-components";
import { ColorOptions } from "@/src/types";
import { MainInner, StyledMainSection } from "@/styles/global";
import { H3Css, SmallHeading1Css } from "@/styles/typography";

type ColorProps = {
  $textColor: ColorOptions;
};

type TextColorProps = {
  $color?: "black" | "creme" | "brown";
};

type BackdropProps = {
  $background?: ColorOptions;
};

type RowProps = {
  $color: ColorOptions;
  $isOpen: boolean;
  $background: boolean;
  $colorPlacement: "items" | "background";
};

type WrapperProps = {
  $placement: "left" | "right";
  $textAlignment: "center" | "top";
};

const Inner = styled(MainInner)`
  display: flex;
`;

const Backdrop = styled.div<BackdropProps>`
  background-color: ${({ theme, $background }) =>
    theme.colors[$background ?? "transparent"]};
  height: 100%;
  padding: ${({ $background }) => ($background ? " 72px 0" : 0)};

  @media (min-width: ${({ theme }) => theme.breakpoints.xl}) {
    padding: ${({ $background }) => ($background ? " 88px 0" : 0)};
  }
`;

const Wrapper = styled.div<WrapperProps>`
  display: flex;
  flex-direction: column;
  gap: 44px;
  width: 100%;

  @media (min-width: ${({ theme }) => theme.breakpoints.xl}) {
    flex-direction: ${({ $placement }) =>
      $placement === "left" ? "row-reverse" : "row"};
    gap: 32px;
    justify-content: space-between;
    align-items: ${({ $textAlignment }) =>
      $textAlignment === "top" ? "flex-start" : "center"};
  }
`;

const TextWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  flex-grow: 1;

  h2 {
    hyphens: auto;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.xl}) {
    min-width: 50%;
    max-width: 50%;
  }
`;

const Rows = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  flex-grow: 1;
  flex-shrink: 0;

  @media (min-width: ${({ theme }) => theme.breakpoints.xl}) {
    min-width: 45%;
    max-width: 45%;
  }
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const RowWrapper = styled.div`
  display: flex;
  width: 100%;
`;

const Row = styled.div<RowProps>`
  background: ${({ theme, $color }) => theme.colors[$color ?? "brown"]};
  margin-bottom: ${({ $background, $colorPlacement }) =>
    $colorPlacement === "background" ? "8px" : $background ? 0 : "8px"};
  padding: ${({ $background }) => ($background ? "16px" : "24px")};
  border-radius: ${({ $background, $colorPlacement }) =>
    $colorPlacement === "background" ? "10px" : $background ? 0 : "10px"};
  border-bottom: ${({ $background }) =>
    $background ? "1px solid #fcf3e84d" : "none"};
  word-break: break-all;
  position: relative;
  overflow: hidden;

  transition: all 0.2s ease-in-out;

  border: ${({ $colorPlacement, theme, $color }) =>
    $colorPlacement === "background"
      ? $color === "black"
        ? `1px solid #fcf3e84d`
        : `1px solid ${theme.colors.black}`
      : "none"};

  @media (min-width: ${({ theme }) => theme.breakpoints.xl}) {
    padding: ${({ $background }) => ($background ? "16px 24px" : "24px")};
    &:hover {
      opacity: ${({ $isOpen }) => ($isOpen ? 1 : 0.9)};
    }
  }
`;

const SubRow = styled.div`
  padding-top: 48px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;

  * {
    word-break: keep-all;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.xl}) {
    padding-top: 72px;
  }
`;

const IconWrapper = styled.div`
  background-color: ${({ theme }) => theme.colors.orange};
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Section = styled(StyledMainSection)<ColorProps>`
  ${IconWrapper} {
    svg {
      color: ${({ theme, $textColor }) => theme.colors[$textColor]} !important;
    }
  }
`;

const AccordionTitleWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  cursor: pointer;
  position: relative;
`;

const AccordionTitle = styled.h3<TextColorProps>`
  ${H3Css}
  color: ${({ theme, $color }) => theme.colors[$color || "black"]};
  width: 100%;
`;

const TitleCss = css<TextColorProps>`
  font-family: "Herbus Wirepas Bold";
  color: ${({ theme, $color }) => theme.colors[$color || "black"]};
  line-height: 80%;
  font-style: normal;
  font-weight: 700;
  margin-bottom: 24px;
`;

const Title = styled.h2<TextColorProps>`
  ${TitleCss}
  font-size: 60px;
  letter-spacing: 3px;

  width: 100%;
  @media screen and (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    font-size: 30px;
    letter-spacing: 1.5px;
  }
`;

const TitleMedium = styled.h2<TextColorProps>`
  ${TitleCss}
  font-size: 80px;
  letter-spacing: 4px;

  @media screen and (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    font-size: 40px;
    letter-spacing: 2px;
  }
`;

const TitleLarge = styled.h2<TextColorProps>`
  ${TitleCss}
  font-size: 100px;
  letter-spacing: 5px;

  @media screen and (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    font-size: 50px;
    letter-spacing: 2.5px;
  }
`;

const TitleSmall = styled.p<TextColorProps>`
  ${SmallHeading1Css}
  color: ${({ theme, $color }) => theme.colors[$color || "black"]};
  margin-bottom: 8px;
`;

const S = {
  Inner,
  Backdrop,
  Wrapper,
  TextWrapper,
  Rows,
  ContentWrapper,
  RowWrapper,
  Row,
  SubRow,
  IconWrapper,
  Section,
  AccordionTitleWrapper,
  AccordionTitle,
  Title,
  TitleMedium,
  TitleLarge,
  TitleSmall,
};

export default S;
